import { useState } from 'react'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'

import {
  RouteAdminPartners,
  RouteAdminProviders, RouteAdminStatusCollections,
  RouteAdminSharedFieldMaps, RouteAsyncJobs,
  RouteFeatureFlags, RouteAdminPartnersTicketingSystems, RouteAdminProviderQueries,
  RouteAdminMimeTypeCollections, RouteMaintenanceTasks, RouteAdminProviderTicketBoards,
  RouteDatabasePerformance, RouteAdminSatClientsPage, RouteAdminProviderCredentials,
} from 'routes'

import { useLocation, matchPath, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { capitalizeFirstLetter } from 'utils/strings'

import MegaMenu from './MegaMenu/Index'
import MegaMenuSection from './MegaMenu/MegaMenuSection'
import MegaMenuColumn from './MegaMenu/MegaMenuColumn'
import MegaMenuRowsGroup from './MegaMenu/MegaMenuRowsGroup'
import MegaMenuSubMenu from './MegaMenu/MegaMenuSubMenu'
import MegaMenuLinkToSubMenu from './MegaMenu/MegaMenuLinkToSubMenu'
import MegaMenuLink from './MegaMenu/MegaMenuLink'
import MegaMenuNoData from './MegaMenu/MegaMenuNoData'

const NavConfiguration = () => {
  const { partnerId, ticketProviderId } = useParams()

  const location = useLocation()
  const { t } = useTranslation()
  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const partnersList = useSelector(sessionUserSelector.megaMenu).partners
  const providersList = useSelector(sessionUserSelector.megaMenu).providers
  const sharedFieldsMaps = useSelector(sessionUserSelector.megaMenu).sharedFieldsMaps
  const statusCollections = useSelector(sessionUserSelector.megaMenu).statusCollections
  const mimeTypeCollections = useSelector(sessionUserSelector.megaMenu).mimeTypeCollections
  const matchConfigurationPage = Boolean(
    matchPath({ path: RouteAdminPartners(), end: false }, location.pathname) ||
    matchPath({ path: RouteAdminSharedFieldMaps(), end: false }, location.pathname) ||
    matchPath({ path: RouteAdminProviders(), end: false }, location.pathname) ||
    matchPath({ path: RouteAdminStatusCollections(), end: false }, location.pathname) ||
    matchPath({ path: RouteAdminMimeTypeCollections(), end: false }, location.pathname)
  )

  const [selectedPartnerId, setSelectedPartnerId] = useState(partnerId || null)
  const [selectedTicketProviderId, setSelectedTicketProviderId] = useState(ticketProviderId || null)

  const handlePartnerClick = (partnerId) => {
    setSelectedPartnerId(partnerId)
  }

  const handleTicketProviderClick = (ticketProviderId) => {
    setSelectedTicketProviderId(ticketProviderId)
  }

  const handleCloseMegaMenu = () => {
    setSelectedPartnerId(partnerId || null)
    setSelectedTicketProviderId(ticketProviderId || null)
  }

  const partnerSection = () => {
    return (
      <MegaMenuSection title={t('mainHeader.configSectionPartner')}>
        <MegaMenuColumn>
          <MegaMenuRowsGroup title={t('mainHeader.partners')} isHighlighted to={RouteAdminPartners()}>
            {!partnersList.length &&
              <MegaMenuNoData />
            }
            {partnersList.map((partner, index) => {
              return (
                <MegaMenuLinkToSubMenu
                  key={partner.id}
                  name={capitalizeFirstLetter(partner.name)}
                  itemId={partner.id}
                  onClickHandler={handlePartnerClick}
                  to={RouteAdminPartners({ partnerId: partner.id })}
                  active={partner.id === selectedPartnerId}
                  icon={partner.icon}
                />
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>

        <MegaMenuColumn>
          <MegaMenuRowsGroup
            title={t('mainHeader.configTicketingSystems')}
            to={selectedPartnerId ? RouteAdminPartnersTicketingSystems({ partnerId: selectedPartnerId }) : null}
          >
            {partnersList.map((partner, index) => {
              return (
                <MegaMenuSubMenu
                  key={partner.id}
                  visible={partner.id === selectedPartnerId}
                >
                  {!partner.ticketingSystems.length &&
                    <MegaMenuNoData />
                  }
                  {partner.ticketingSystems.map((ts) => {
                    return (
                      <MegaMenuLink
                        key={ts.displayId}
                        name={ts.name}
                        to={RouteAdminPartnersTicketingSystems({ partnerId: partner.id, ticketingSystemDisplayId: ts.displayId })}
                      />
                    )
                  })}
                </MegaMenuSubMenu>
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>
      </MegaMenuSection>
    )
  }

  const engineSection = () => {
    return (
      <MegaMenuSection title={t('mainHeader.configSectionEngine')}>
        <MegaMenuColumn>
          <MegaMenuRowsGroup title={t('mainHeader.configTicketProviders')} isHighlighted to={RouteAdminProviders()}>
            {!providersList.length &&
              <MegaMenuNoData />
            }
            {providersList.map((provider, index) => {
              return (
                <MegaMenuLinkToSubMenu
                  key={provider.id}
                  name={provider.name}
                  to={RouteAdminProviders({ ticketProviderId: provider.id })}
                  itemId={provider.id}
                  onClickHandler={handleTicketProviderClick}
                  active={selectedTicketProviderId === provider.id}
                />
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>

        <MegaMenuColumn>
          <MegaMenuRowsGroup
            title={t('mainHeader.configQueries')}
            to={selectedTicketProviderId ? RouteAdminProviderQueries({ ticketProviderId: selectedTicketProviderId }) : null}
          >
            {providersList.map((provider) => {
              return (
                <MegaMenuSubMenu
                  key={provider.id}
                  visible={provider.id === selectedTicketProviderId}
                >
                  {!provider.queries.length &&
                    <MegaMenuNoData />
                  }
                  {provider.queries.map((query) => {
                    return (
                      <MegaMenuLink
                        key={query.id}
                        name={query.name}
                        to={RouteAdminProviderQueries({ ticketProviderId: provider.id, queryId: query.id })}
                      />
                    )
                  })}
                </MegaMenuSubMenu>
              )
            })}
          </MegaMenuRowsGroup>

          <MegaMenuRowsGroup
            title={t('mainHeader.configProviderTicketBoards')}
            to={selectedTicketProviderId ? RouteAdminProviderTicketBoards({ ticketProviderId: selectedTicketProviderId }) : null}
          >
            {providersList.map((provider) => {
              return (
                <MegaMenuSubMenu
                  key={provider.id}
                  visible={provider.id === selectedTicketProviderId}
                >
                  {!provider.providerTicketBoards.length &&
                    <MegaMenuNoData />
                  }
                  {provider.providerTicketBoards.map((providerTicketBoard) => {
                    return (
                      <MegaMenuLink
                        key={providerTicketBoard.id}
                        name={providerTicketBoard.name}
                        to={RouteAdminProviderTicketBoards({ ticketProviderId: provider.id, providerTicketBoardId: providerTicketBoard.id })}
                      />
                    )
                  })}
                </MegaMenuSubMenu>
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>
      </MegaMenuSection>
    )
  }

  const otherSettingsSection = () => {
    return (
      <MegaMenuSection title={t('mainHeader.configSectionOtherSettings')}>
        <MegaMenuColumn>
          <MegaMenuRowsGroup
            title={t('mainHeader.configSharedMaps')}
            to={RouteAdminSharedFieldMaps()}
          >
            {!sharedFieldsMaps.length &&
              <MegaMenuNoData />
            }
            {sharedFieldsMaps.map((sharedFieldsMap) => {
              return (
                <MegaMenuLink
                  key={sharedFieldsMap.id}
                  name={sharedFieldsMap.name}
                  to={RouteAdminSharedFieldMaps({ sharedFieldsMapId: sharedFieldsMap.id })}
                />
              )
            })}
          </MegaMenuRowsGroup>

          <MegaMenuRowsGroup
            title={t('mainHeader.configStatusCollections')}
            to={RouteAdminStatusCollections()}
          >
            {!statusCollections.length &&
              <MegaMenuNoData />
            }
            {statusCollections.map((statusCollection) => {
              return (
                <MegaMenuLink
                  key={statusCollection.id}
                  name={statusCollection.name}
                  to={RouteAdminStatusCollections({ statusCollectionId: statusCollection.id })}
                />
              )
            })}
          </MegaMenuRowsGroup>
          <MegaMenuRowsGroup
            title={t('mainHeader.configMimeTypeCollections')}
            to={RouteAdminMimeTypeCollections()}
          >
            {!mimeTypeCollections.length &&
              <MegaMenuNoData />
            }
            {mimeTypeCollections.map((mimeTypeCollection) => {
              return (
                <MegaMenuLink
                  key={mimeTypeCollection.id}
                  name={mimeTypeCollection.name}
                  to={RouteAdminMimeTypeCollections({ mimeTypeCollectionId: mimeTypeCollection.id })}
                />
              )
            })}
          </MegaMenuRowsGroup>

          <MegaMenuRowsGroup title={t('mainHeader.configSectionAuthentication')}>
            {userIsAdmin && (
              <>
                <MegaMenuLink name={t('mainHeader.providerCredentials')} to={RouteAdminProviderCredentials()} />
              </>
            )}
            {userIsAdmin && Boolean(process.env.SAT_API_ENABLED === 'true') && (
              <MegaMenuLink name={t('mainHeader.satClients')} to={RouteAdminSatClientsPage()} />
            )}
          </MegaMenuRowsGroup>

          <MegaMenuRowsGroup title={t('mainHeader.configSectionInternal')}>
            {userIsAdmin && (
              <>
                <MegaMenuLink name={t('mainHeader.asyncJobs')} to={RouteAsyncJobs()} target='_blank' />
                <MegaMenuLink name={t('mainHeader.featureFlags')} to={RouteFeatureFlags()} target='_blank' />
                <MegaMenuLink name={t('mainHeader.maintenanceTasks')} to={RouteMaintenanceTasks()} target='_blank' />
                <MegaMenuLink name={t('mainHeader.databasePerformance')} to={RouteDatabasePerformance()} target='_blank' />
              </>
            )}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>
      </MegaMenuSection>
    )
  }

  return (
    <MegaMenu
      active={matchConfigurationPage}
      title={t('mainHeader.configuration')}
      handleClose={handleCloseMegaMenu}
    >
      {partnerSection()}
      {engineSection()}
      {otherSettingsSection()}
    </MegaMenu>
  )
}

export default NavConfiguration
